import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, Store } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useBusinesses } from '@/features/business/use-businesses';

/**
 * The Global dashboard's store filter. An empty selection means "every
 * store" rather than "no store selected" — an empty multi-select otherwise
 * reads as "show nothing", which is never what a manager wants on load.
 *
 * Rendered as a FilterBar custom field, so it carries no label of its own and
 * fills the width it is given.
 */
export function StoreMultiSelect({
  id,
  selected,
  onChange,
  disabled,
}: {
  id?: string;
  selected: string[];
  onChange: (ids: string[]) => void;
  /** Locked while the dashboard it drives is fetching. */
  disabled?: boolean;
}) {
  const { data: businesses, isPending } = useBusinesses();
  const { t } = useTranslation();

  if (isPending) {
    return <Skeleton className="h-9 w-full" />;
  }

  const options = businesses ?? [];
  const label =
    selected.length === 0
      ? t('dashboard.allStores')
      : selected.length === 1
        ? (options.find((b) => b.id === selected[0])?.name ?? t('dashboard.oneStore'))
        : t('dashboard.nStores', { count: selected.length });

  const toggle = (id: string) =>
    onChange(
      selected.includes(id)
        ? selected.filter((s) => s !== id)
        : [...selected, id]
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          id={id}
          type="button"
          disabled={disabled}
          className="bg-background hover:bg-accent/40 focus-visible:ring-ring/50 flex h-9 w-full items-center gap-2 rounded-md border px-3 text-left text-sm outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Store className="text-muted-foreground size-4 shrink-0" />
          <span className="min-w-0 flex-1 truncate">{label}</span>
          <ChevronsUpDown className="text-muted-foreground/50 size-4 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>{t('dashboard.filterByStore')}</DropdownMenuLabel>
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); onChange([]); }}>
          <span className="flex-1">{t('dashboard.allStores')}</span>
          {selected.length === 0 && (
            <span className="text-primary text-xs font-medium">
              {t('dashboard.active')}
            </span>
          )}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {options.length === 0 && (
          <p className="text-muted-foreground px-2 py-3 text-xs">
            {t('dashboard.noStores')}
          </p>
        )}
        {options.map((business) => (
          <DropdownMenuCheckboxItem
            key={business.id}
            checked={selected.includes(business.id)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => toggle(business.id)}
          >
            {business.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
